import { ChangeEvent, useRef } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef, GridApi } from 'ag-grid-community';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import { LayerApiModel, WithSymbol, WithTemplate } from '../../../types/models/layer.model';
import { TrimbleLayerApiModel, TrimbleLayerRelationship, WithLayer } from '../../../types/models/trimble-layer.model';
import { SymbolApiModel } from '../../../types/models/symbol.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchTrimbleLayers } from '../api/trimble-layers.api';
import {
  useDeleteTrimbleLayer,
  useExportTrimbleLayers,
  useImportTrimbleLayers,
} from '../hooks/api/use-trimble-layers.hook';
import { LoadingIndicator } from '../components/LoadingIndicator';

export const TrimbleLayers = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();
  const gridApi = useRef<GridApi>();

  const [exportTrimbleLayers, { loading: exportingTrimbleLayers, error: exportTrimbleLayersError }] =
    useExportTrimbleLayers();
  const [importTrimbleLayers, { loading: importingTrimbleLayers, error: importTrimbleLayersError }] =
    useImportTrimbleLayers();
  const [deleteTrimbleLayer, { loading: deletingTrimbleLayer, error: deleteTrimbleLayerError }] =
    useDeleteTrimbleLayer();

  const handleImportFromFile = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) {
      return;
    }

    await importTrimbleLayers({ file: e.target.files[0] });
    gridApi.current?.refreshInfiniteCache();
  };

  const columns: ColDef<TrimbleLayerApiModel & WithSymbol & WithTemplate & WithLayer>[] = [
    {
      field: 'name',
      colId: 'layer.name',
      valueGetter: ({ data }) => data?.layer.name,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'color',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      cellRenderer: ({ node }: CustomCellRendererProps<TrimbleLayerApiModel>) => (
        <Box display="flex" alignItems="center" sx={{ height: '100%' }}>
          <Box
            sx={{
              backgroundColor:
                node.data?.color && node.data?.color.includes('#') ? node.data?.color : `#${node.data?.color}`,
              height: '50%',
              width: '50%',
              border: '1px solid #333',
            }}
          />
        </Box>
      ),
    },
    {
      colId: 'layer.symbolLinetype.name',
      field: 'symbol',
      headerName: 'Line Style',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
      valueGetter: ({ data }) => (data?.layer?.symbol as SymbolApiModel)?.name ?? '',
    },
    {
      field: 'lineweight',
      headerName: 'Line Weight',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['equals'], maxNumConditions: 1 },
    },
    {
      field: 'isProtected',
      headerName: 'Protected',
      sortable: true,
      filter: true,
      cellDataType: 'boolean',
      filterParams: {
        values: [true, false],
        valueFormatter: (params: { value: boolean }) => (params.value ? 'Yes' : 'No'),
      },
      cellRenderer: ({ node }: CustomCellRendererProps<TrimbleLayerApiModel>) => (
        <Checkbox checked={node.data?.isProtected} disabled />
      ),
    },
    {
      field: 'groupLayer',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'displayPriority',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['equals'], maxNumConditions: 1 },
    },
    {
      field: 'isPrint',
      headerName: 'Print',
      sortable: true,
      filter: true,
      cellDataType: 'boolean',
      filterParams: {
        values: [true, false],
        valueFormatter: (params: { value: boolean }) => (params.value ? 'Yes' : 'No'),
      },
      cellRenderer: ({ node }: CustomCellRendererProps<TrimbleLayerApiModel>) => (
        <Checkbox checked={node.data?.isPrint} disabled />
      ),
    },
    {
      headerName: 'Actions',
      minWidth: 200,
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<TrimbleLayerApiModel & WithLayer>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deleteTrimbleLayer({ trimbleLayerId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Trimble Layer',
                message: `Are you sure you want to delete Trimble layer "${(node.data?.layer as LayerApiModel).name}"?`,
                error: deleteTrimbleLayerError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getTrimbleLayers: Parameters<typeof InfniteScrollingGrid<TrimbleLayerApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterModel,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchTrimbleLayers({
      ...filterQuery,
      isProtected: filterModel?.isProtected?.type,
      isPrint: filterModel?.isPrint?.type,
      include: [TrimbleLayerRelationship.LAYER, TrimbleLayerRelationship.SYMBOL, TrimbleLayerRelationship.TEMPLATE],
      cursor,
      limit,
      sort: sortQuery,
    });

    return data;
  };

  const renderLoadingIndicator = () => {
    if (exportingTrimbleLayers || importingTrimbleLayers || deletingTrimbleLayer) {
      return (
        <Dialog open>
          <LoadingIndicator />
        </Dialog>
      );
    }

    return null;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      {renderLoadingIndicator()}
      {importTrimbleLayersError || exportTrimbleLayersError ? (
        <Alert severity="error">{(importTrimbleLayersError || exportTrimbleLayersError)?.response?.data.message}</Alert>
      ) : null}
      <Helmet title="Trimble Layer Manager" />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Trimble Layer Manager</Typography>
          <Box flexGrow={1} />
          <Button
            sx={{ mr: 2 }}
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`add`)}
          >
            Add Layer
          </Button>
          <Button
            sx={{ mr: 2 }}
            color="primary"
            variant="contained"
            component="label"
            startIcon={<FileUploadIcon />}
            disabled={importingTrimbleLayers}
          >
            Import
            <input type="file" hidden onChange={handleImportFromFile} accept=".layer" />
          </Button>
          <Button
            color="primary"
            variant="contained"
            component="label"
            startIcon={<FileDownloadIcon />}
            disabled={exportingTrimbleLayers}
            onClick={() => exportTrimbleLayers()}
          >
            Export
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid
            onGridReady={({ api }) => {
              gridApi.current = api;
            }}
            localeText={{
              true: 'Yes',
              false: 'No',
            }}
            loading={deletingTrimbleLayer}
            columnDefs={columns}
            onGetRows={getTrimbleLayers}
          />
        </Box>
      </Box>
    </Box>
  );
};
